import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { getTransferFrequency } from '@/modules/entities/details/HypotheticalTransfersCard/HypotheticalTransfersCard.utils';
import { getHypotheticalInsuranceTransferYearsString } from '@/modules/entities/details/HypotheticalTransfersCard/StrategyItemsTable.utils';
import { InsurancePolicyTypeCopyMap } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { EstateWaterfall_TransferLogItemFragment } from '@/modules/estateWaterfall/graphql/EstateWaterfall.generated';
import { FUNCTIONAL } from '@/styles/tokens/colors';
import {
  EstateWaterfallTransferKind,
  HypotheticalInsuranceTransferFrequency,
  InsurancePolicyKind,
} from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { getTransferDateDisplay } from '@/utils/hypotheticalTransfers/transferUtils';

import { EdgeLabelCard, EdgeLabelCardProps } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface CreateTransferCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  transfer: EstateWaterfall_TransferLogItemFragment;
  firstGrantorDeathYear?: number | null;
  secondGrantorDeathYear?: number | null;
  firstGrantorId?: string;
  secondGrantorId?: string;
}

function getTransferProps({
  sourceEntityName,
  targetEntityName,
  transfer,
  firstGrantorDeathYear,
  secondGrantorDeathYear,
  firstGrantorId,
  secondGrantorId,
}: CreateTransferCardParams): EdgeLabelCardProps {
  const associatedHypotheticalTransfer = (() => {
    if (
      transfer.associatedObject?.__typename ===
      'EstateWaterfallHypotheticalTransfer'
    ) {
      return transfer.associatedObject;
    }

    return null;
  })();

  const associatedHypotheticalInsuranceTransfer = (() => {
    if (transfer.associatedObject?.__typename === 'HypotheticalInsurance') {
      return transfer.associatedObject;
    }

    return null;
  })();

  if (
    transfer.kind === EstateWaterfallTransferKind.HypotheticalInsurancePolicy &&
    associatedHypotheticalInsuranceTransfer
  ) {
    const insurancePolicy =
      associatedHypotheticalInsuranceTransfer.createdInsurancePolicy ??
      associatedHypotheticalInsuranceTransfer.existingInsurancePolicy;

    const policyKind = insurancePolicy?.kind;
    let policyTypeLabel = EMPTY_CONTENT_HYPHEN;
    if (policyKind) {
      policyTypeLabel =
        InsurancePolicyTypeCopyMap[policyKind] || EMPTY_CONTENT_HYPHEN;
    }

    const termEndValue = (() => {
      if (
        policyKind === InsurancePolicyKind.Term &&
        insurancePolicy?.termDurationYears &&
        insurancePolicy?.startDate
      ) {
        const startYear = new Date(insurancePolicy.startDate).getFullYear();
        return (startYear + insurancePolicy.termDurationYears).toString();
      }

      // If the policy is whole life, display the latest death year (as defined on the waterfall)
      // of the insured grantors on the policy. If the insured person is not a
      // waterfall grantor (e.g. a different client), fall back gracefully.
      if (policyKind === InsurancePolicyKind.Whole) {
        const insuredGrantorIDs =
          insurancePolicy?.policyHolders
            ?.map((holder) => holder?.id)
            .filter((id): id is string => id != null) ?? [];

        const relevantDeathYears: number[] = [];
        if (firstGrantorId && insuredGrantorIDs.includes(firstGrantorId)) {
          if (firstGrantorDeathYear) {
            relevantDeathYears.push(firstGrantorDeathYear);
          }
        }
        if (secondGrantorId && insuredGrantorIDs.includes(secondGrantorId)) {
          if (secondGrantorDeathYear) {
            relevantDeathYears.push(secondGrantorDeathYear);
          }
        }

        if (relevantDeathYears.length > 0) {
          return Math.max(...relevantDeathYears).toString();
        }

        // Policy holder is not a waterfall grantor — no death year to display
        return EMPTY_CONTENT_HYPHEN;
      }

      return EMPTY_CONTENT_HYPHEN;
    })();

    return {
      title: 'Hypothetical policy transfer',
      titleSx: { color: FUNCTIONAL.WARNING[600] },
      description: `${sourceEntityName} transfers an insurance policy to ${targetEntityName}.`,
      rows: compact([
        {
          label: 'Policy type',
          value: policyTypeLabel,
        },
        {
          label: 'Insurance carrier',
          value: insurancePolicy?.carrierName ?? EMPTY_CONTENT_HYPHEN,
        },
        {
          label: 'Term end',
          value: termEndValue,
        },
        {
          label: 'Cash value',
          value: formatCurrencyNoDecimals(
            insurancePolicy?.cashValue ?? new Decimal(0)
          ),
        },
        {
          label: 'Death benefit amount',
          value: insurancePolicy?.deathBenefitAmount
            ? formatCurrencyNoDecimals(
                new Decimal(insurancePolicy.deathBenefitAmount)
              )
            : EMPTY_CONTENT_HYPHEN,
        },
      ]),
    };
  }

  if (
    transfer.kind ===
      EstateWaterfallTransferKind.HypotheticalInsurancePolicyFunding &&
    associatedHypotheticalInsuranceTransfer
  ) {
    const yearValue = getHypotheticalInsuranceTransferYearsString(
      associatedHypotheticalInsuranceTransfer
    );

    return {
      title: associatedHypotheticalInsuranceTransfer?.displayName ?? 'Transfer',
      titleSx: { color: FUNCTIONAL.WARNING[600] },
      description: `From ${sourceEntityName} to ${targetEntityName}`,
      rows: compact([
        {
          label: 'Transfer amount',
          value: formatCurrencyNoDecimals(
            transfer.finalTransferAmount ?? new Decimal(0)
          ),
        },
        {
          label: 'Transfer frequency',
          value:
            associatedHypotheticalInsuranceTransfer.transferFrequency ===
            HypotheticalInsuranceTransferFrequency.OneTime
              ? 'One-time'
              : 'Recurring',
        },
        {
          label: 'Year(s)',
          value: yearValue,
        },
      ]),
    };
  }

  const yearValue = (() => {
    if (associatedHypotheticalTransfer) {
      return getTransferDateDisplay({
        startYear: associatedHypotheticalTransfer.startYear,
        endYear: associatedHypotheticalTransfer.endYear,
      });
    }

    if (transfer.year) {
      return transfer.year.toString();
    }

    return null;
  })();

  return {
    title: associatedHypotheticalTransfer?.name ?? 'Transfer',
    titleSx: { color: FUNCTIONAL.WARNING[600] },
    description: `From ${sourceEntityName} to ${targetEntityName}.`,
    rows: compact([
      {
        label: 'Transfer amount',
        value: formatCurrencyNoDecimals(
          transfer.finalTransferAmount ?? new Decimal(0)
        ),
      },
      associatedHypotheticalTransfer
        ? {
            label: 'Transfer frequency',
            value: getTransferFrequency({
              startYear: associatedHypotheticalTransfer.startYear,
              endYear: associatedHypotheticalTransfer.endYear,
            }),
          }
        : null,
      yearValue
        ? {
            label: 'Year(s)',
            value: yearValue,
          }
        : null,
    ]),
  };
}

export function createTransferCard({
  sourceEntityName,
  targetEntityName,
  transfer,
  firstGrantorDeathYear,
  secondGrantorDeathYear,
  firstGrantorId,
  secondGrantorId,
}: CreateTransferCardParams): CardConfig {
  const label =
    transfer.kind === EstateWaterfallTransferKind.HypotheticalInsurancePolicy
      ? 'Policy transfer'
      : 'Transfer';

  return {
    element: (
      <EdgeLabelCard
        key={transfer.associatedObjectID ?? ''}
        {...getTransferProps({
          sourceEntityName,
          targetEntityName,
          transfer,
          firstGrantorDeathYear,
          secondGrantorDeathYear,
          firstGrantorId,
          secondGrantorId,
        })}
      />
    ),
    label,
    value: formatCurrencyNoDecimals(
      transfer.finalTransferAmount ?? new Decimal(0)
    ),
    variant: 'secondary',
  };
}
